var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/date.ts
import { format, formatDistanceToNow, subYears } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import parseJSON from "date-fns/parseJSON";
var DATE_FORMAT = "Y-MM-dd";
var DATE_OPTION_FORMAT = "MMM-dd-yy";
var DATE_OPTION__ALT_FORMAT = "dd MMM yy";
var DATE_TIME_FORMAT = "Y-MM-dd H:mm:ss";
var UTC_TIMEZONE = "UTC";
function getDate(date) {
  const result = date ? new Date(date) : new Date();
  return result;
}
function getMinAge() {
  return format(subYears(new Date(), 18), DATE_FORMAT);
}
function getCurrentDate() {
  return new Date().toISOString().split("T")[0];
}
function fmtDistanceToNow(date) {
  return formatDistanceToNow(date, { includeSeconds: true });
}
function fmtUTCDate(date) {
  return formatInTimeZone(parseJSON(getDate(date)), UTC_TIMEZONE, DATE_FORMAT);
}
function fmtUTCDateTime(date) {
  return formatInTimeZone(parseJSON(getDate(date)), UTC_TIMEZONE, DATE_TIME_FORMAT);
}
function fmtLocalDateTime(date) {
  return formatInTimeZone(getDate(date), Intl.DateTimeFormat().resolvedOptions().timeZone, `${DATE_TIME_FORMAT} zzz`);
}
function fmtUTCDateOptionTime(date) {
  return formatInTimeZone(parseJSON(getDate(date)), UTC_TIMEZONE, DATE_OPTION_FORMAT);
}
function fmtUTCDateOptionTimeAlternative(date) {
  return formatInTimeZone(parseJSON(getDate(date)), UTC_TIMEZONE, DATE_OPTION__ALT_FORMAT);
}
function dateToMs(date) {
  return parseJSON(getDate(date)).getTime();
}

// src/decimal.ts
import Decimal from "decimal.js";
var DISPLAY_PRICE_DECIMALS = 4;
var DISPLAY_USD_DECIMALS = 2;
function getDisplayPriceDecimals(currency) {
  return (currency == null ? void 0 : currency.toUpperCase().startsWith("USD")) ? DISPLAY_USD_DECIMALS : DISPLAY_PRICE_DECIMALS;
}
function toDecimal(value) {
  const str = `${value}`.trim();
  if (!str) {
    return null;
  }
  try {
    const decimal = new Decimal(str);
    if (!decimal.isFinite() || decimal.isNaN()) {
      return null;
    }
    return decimal;
  } catch {
    return null;
  }
}
function numeralFormatter(value, decimals = DISPLAY_PRICE_DECIMALS, maximumDecimals) {
  const decimal = toDecimal(value);
  if (!decimal || decimal.isZero()) {
    return "0";
  }
  const maxDecimals = maximumDecimals != null ? maximumDecimals : decimals;
  const rounded = decimal.toDecimalPlaces(maxDecimals, Decimal.ROUND_HALF_UP);
  return Intl.NumberFormat(navigator.language, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: maxDecimals
  }).format(rounded.toNumber());
}
function decimalFormatter(value, decimals = 4, maximumDecimals) {
  return numeralFormatter(value, decimals, maximumDecimals != null ? maximumDecimals : DISPLAY_PRICE_DECIMALS);
}
function millionsFormatter(value, decimals = 0) {
  const decimal = toDecimal(value);
  if (!decimal || decimal.isZero()) {
    return "";
  }
  const negative = decimal.isNegative();
  const valueParse = numeralFormatter(decimal.abs().div(1e6).toString(), decimals);
  return `${negative ? "-" : ""}$${valueParse}mm`;
}

// src/file.ts
function downloadFile(data, filename) {
  const url = window.URL.createObjectURL(data);
  const a = document.createElement("a");
  document.body.appendChild(a);
  a.style.display = "none";
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

// src/form.ts
import get from "lodash/get";
function registerField(name, form, ignoreUntouched = false, options) {
  const {
    register,
    formState: { errors, dirtyFields }
  } = form;
  const dirty = get(dirtyFields, name);
  const error = ignoreUntouched && !dirty ? null : get(errors, name);
  return __spreadProps(__spreadValues({}, register(name, options)), {
    error: !!error,
    errorLabel: `${(error == null ? void 0 : error.message) || ""}`
  });
}
function registerCustomField(name, form) {
  const {
    setValue,
    formState: { errors }
  } = form;
  const error = get(errors, name);
  return {
    onChange: (e) => {
      setValue(name, e == null ? void 0 : e.value, { shouldValidate: true, shouldTouch: true });
    },
    error: !!error,
    errorLabel: `${(error == null ? void 0 : error.message) || ""}`
  };
}

// src/validation.ts
import get2 from "lodash/get";
function parseValidationMessage(result) {
  var _a;
  const errors = get2(result, "error.errors", []);
  return (_a = errors.map((err) => err.message)) == null ? void 0 : _a.join("\n");
}
export {
  DISPLAY_PRICE_DECIMALS,
  DISPLAY_USD_DECIMALS,
  dateToMs,
  decimalFormatter,
  downloadFile,
  fmtDistanceToNow,
  fmtLocalDateTime,
  fmtUTCDate,
  fmtUTCDateOptionTime,
  fmtUTCDateOptionTimeAlternative,
  fmtUTCDateTime,
  getCurrentDate,
  getDisplayPriceDecimals,
  getMinAge,
  millionsFormatter,
  numeralFormatter,
  parseValidationMessage,
  registerCustomField,
  registerField
};
